import { esApp } from './plataforma';

/**
 * Los diálogos de pantalla. `.fixed` deja fuera los popovers, que también son `role="dialog"` pero
 * los coloca un envoltorio: un buscador dentro de un desplegable sí debe recibir su foco.
 */
const DIALOGO = '[role="dialog"].fixed, [role="alertdialog"].fixed';

/** Los `<input>` que no abren teclado: el foco en ellos no molesta. */
const SIN_TECLADO = [
	'button',
	'checkbox',
	'color',
	'file',
	'image',
	'radio',
	'range',
	'reset',
	'submit'
];

const abreTeclado = (elemento: Element): boolean =>
	elemento instanceof HTMLTextAreaElement ||
	(elemento instanceof HTMLInputElement && !SIN_TECLADO.includes(elemento.type));

/**
 * Cuánto dura «la apertura» de un diálogo, contada desde su primer foco. Radix enfoca el primer
 * campo y acto seguido lo selecciona, y en iPhone `select()` lo vuelve a enfocar 2 ms después: la
 * apertura es una ráfaga de focos, no uno. 300 ms la cubren con margen y siguen siendo menos de lo
 * que tarda nadie en tocar un campo de un diálogo que acaba de aparecer.
 */
const APERTURA_MS = 300;

/**
 * Que abrir un diálogo no saque el teclado.
 *
 * Al abrirse, un diálogo da el foco a su primer elemento. Cuando ese elemento es un campo de texto,
 * en la app salta el teclado sin que nadie lo haya pedido y iOS desplaza la vista para enseñar el
 * campo: el título y el botón de cerrar quedan fuera de la pantalla por arriba (medido en la
 * calculadora de rescate: 68 px solo con la barra del teclado).
 *
 * Se interviene solo durante la apertura de cada diálogo: si en ese instante el foco cae en un
 * campo de texto, pasa al propio diálogo, que es donde estaría si su primer elemento no fuera un
 * campo. Cualquier foco posterior —un toque, un lector de pantalla, un botón del diálogo que
 * enfoca su campo, el tabulador— no se toca.
 *
 * Solo en la app: en la web el foco inicial de los diálogos no cambia. Devuelve cómo desinstalarlo.
 */
export const noAbrirTecladoAlAbrirDialogos = (): (() => void) => {
	if (!esApp()) {
		return () => undefined;
	}

	const primerFoco = new WeakMap<Element, number>();

	const alEnfocar = (evento: FocusEvent) => {
		const enfocado = evento.target as Element;
		const dialogo = enfocado.closest<HTMLElement>(DIALOGO);
		if (!dialogo) {
			return;
		}

		const ahora = performance.now();
		const abiertoEn = primerFoco.get(dialogo) ?? ahora;
		primerFoco.set(dialogo, abiertoEn);

		if (ahora - abiertoEn < APERTURA_MS && abreTeclado(enfocado)) {
			// El diálogo no se enfoca nunca por sí solo, así que no tiene estilo de foco: el navegador
			// le pintaba su contorno alrededor entero (amarillo en Android, azul en iPhone) y parecía
			// «seleccionado». Este foco es solo para que no salte el teclado; no hay nada que señalar.
			dialogo.style.outline = 'none';
			dialogo.focus({ preventScroll: true });
		}
	};

	document.addEventListener('focusin', alEnfocar, true);

	return () => document.removeEventListener('focusin', alEnfocar, true);
};
